/**
 * Обозначение марки для значка машины.
 *
 * Ставятся буквы, а не логотип. Логотип марки — товарный знак: он не переходит
 * в общественное достояние со временем, и «свободной» его версии не бывает —
 * перерисованная звезда остаётся звездой Mercedes. Название марки, набранное
 * шрифтом интерфейса, ничьего знака не воспроизводит.
 *
 * Марка берётся из имени машины и больше ниоткуда. Поле `model` у Traccar
 * занято под модель трекера (FMC150), и от неё зависит, разберёт ли Traccar
 * данные с шины: занять его маркой автомобиля — потерять уровень топлива.
 * Заводить же отдельное поле ради четырёх машин дороже, чем оно стоит.
 *
 * Отсюда правило, которое надо знать при заведении машины: **значок идёт от
 * имени**. «Sprinter 2026» получит MB, «Фургон 1» — обычный грузовик.
 */

import type { ArtId } from './brandArt'

export type Brand = {
  /** Что написать в кружке, если знака нет. Не длиннее четырёх букв. */
  short: string
  /** Кегль в пикселях. Подобран под кружок 21 пиксель. */
  size: number
  /** Нарисованный знак марки. Нет — ставятся буквы. */
  art?: ArtId
}

/**
 * Слова, по которым узнаётся марка: и сама марка, и её модели.
 *
 * Модели нужны потому, что машину назовут «Sprinter 2026», а не
 * «Mercedes-Benz Sprinter 2026» — короткое имя и в списке читается лучше.
 */
const MARKS: ReadonlyArray<readonly [string, readonly string[], ArtId?]> = [
  ['MAN', ['man', 'tge'], 'man'],
  ['MB', ['mercedes', 'mercedes-benz', 'benz', 'sprinter', 'vito', 'citan', 'actros', 'atego'], 'mercedes'],
  ['FIAT', ['fiat', 'ducato', 'e-ducato', 'doblo', 'scudo', 'talento'], 'fiat'],
  ['FORD', ['ford', 'transit', 'tourneo'], 'ford'],
  ['VW', ['vw', 'volkswagen', 'crafter', 'caddy', 'transporter'], 'vw'],
  ['OPEL', ['opel', 'movano', 'vivaro', 'combo'], 'opel'],
  ['REN', ['renault', 'master', 'trafic', 'kangoo'], 'renault'],
  ['PEU', ['peugeot', 'boxer', 'expert', 'partner'], 'peugeot'],
  ['CIT', ['citroen', 'citroën', 'jumper', 'jumpy', 'berlingo'], 'citroen'],
  ['TOY', ['toyota', 'proace', 'hilux'], 'toyota'],
  ['NIS', ['nissan', 'nv400', 'primastar', 'interstar'], 'nissan'],
  ['VOL', ['volvo', 'fh', 'fm'], 'volvo'],
  ['IVE', ['iveco', 'daily'], 'iveco'],
  ['MAX', ['maxus', 'edeliver'], 'maxus'],
  ['DAF', ['daf', 'xf', 'cf', 'lf'], 'daf'],
  ['SCA', ['scania'], 'scania'],
  ['ISU', ['isuzu'], 'isuzu'],
  ['DAC', ['dacia', 'dokker', 'logan'], 'dacia'],
]

/** Кегль под длину: четыре буквы в кружок 21 пиксель влезают только мелкими. */
const SIZES: Record<number, number> = { 2: 12, 3: 10, 4: 8.5 }

/**
 * Марка по имени машины. Ничего не узнали — null, и значок останется обычным.
 *
 * Слова сравниваются целиком, а не вхождением: «Manager» не MAN, а
 * «Transporterschrank» не Transporter. Иначе значок сядет на случайную машину,
 * и человек решит, что ошибся сам.
 */
export function brandOf(name: string | null | undefined): Brand | null {
  if (!name) return null
  // Разделителем считается всё, кроме букв и цифр: «E-Ducato», «Sprinter №2»,
  // «MAN/TGE» должны разбираться одинаково. Дефис при этом не теряется —
  // «e-ducato» проверяется и целиком, и по частям.
  const lower = name.toLowerCase()
  const words = new Set(lower.split(/[^0-9a-zа-яё-]+/).filter(Boolean))
  for (const word of [...words]) {
    for (const part of word.split('-')) if (part) words.add(part)
  }

  for (const [short, keys, art] of MARKS) {
    if (keys.some((key) => words.has(key))) {
      const brand: Brand = { short, size: SIZES[short.length] ?? 10 }
      if (art) brand.art = art
      return brand
    }
  }
  return null
}
